import {Component, ElementRef, inject, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {usagesText} from '../column-usages';
import {ImportSessionStore} from '../import-session.store';
import {BlankValueSuggestion} from './blank-values.model';

/**
 * The Import module's Verdier som betyr tomt tab: the column profile's
 * suggestions for the columns the mapping reads, each read as blank or
 * rejected as a real value by the operator; the values read as blank per
 * column; the rejected ones, each with an undo; and a manual add from a
 * column's most common values.
 */
@Component({
  selector: 'app-import-blank-values-step',
  templateUrl: './import-blank-values-step.component.html',
  styleUrls: ['./import-blank-values-step.component.scss'],
  standalone: false,
})
export class ImportBlankValuesStepComponent {
  readonly store: ImportSessionStore = inject(ImportSessionStore);

  // The manual add's column and value.
  readonly columnControl = new FormControl<string | null>(null);
  readonly valueControl = new FormControl<string>('', {nonNullable: true});
  @ViewChild('valueInput') private valueInput?: ElementRef<HTMLInputElement>;

  /** `suggestion.column` and `suggestion.value`, which tell suggestions
   * apart. */
  suggestionKey(suggestion: BlankValueSuggestion): string {
    return JSON.stringify([suggestion.column, suggestion.value]);
  }

  readonly usagesText = usagesText;

  /** "i N rader", in the singular for one row. */
  rowsKey(count: number): string {
    return count === 1 ? 'TRANS__IMPORT__BLANK_VALUES__ROWS_ONE' : 'TRANS__IMPORT__BLANK_VALUES__ROWS';
  }

  /** "N celler", in the singular for one cell. */
  cellsKey(count: number): string {
    return count === 1 ? 'TRANS__IMPORT__BLANK_VALUES__CELLS_ONE' : 'TRANS__IMPORT__BLANK_VALUES__CELLS';
  }

  /** How many cells a column's values read as blank stand in, as far as
   * the column profile counts them, and where the mapping reads the
   * column; `null` when neither is known. */
  readAsBlankNote(group: {column: string; values: string[]}): {cells: number | null; usages: string} | null {
    const cells = this.store.blankCellCount(group.column, group.values);
    const usages = usagesText(this.store.usagesByColumn.get(group.column));
    return cells === null && !usages ? null : {cells, usages};
  }

  /** The values rejected as blank, by column. */
  get rejectedColumns(): {column: string; values: string[]}[] {
    return Object.entries(this.store.blankValuesRejected).map(([column, values]) => ({column, values}));
  }

  /** The manual add column's most common values. */
  get topValues(): {value: string; count: number}[] {
    const column = this.columnControl.value;
    return (column && this.store.columnProfile?.columns.get(column)?.top) || [];
  }

  get canAdd(): boolean {
    const column = this.columnControl.value;
    const value = this.valueControl.value.trim();
    return !!column && !!value && !this.store.isReadAsBlank(column, value);
  }

  /** The manual add, which empties the value for the next one. */
  add(): void {
    if (this.canAdd) {
      this.store.markBlankValue(this.columnControl.value!, this.valueControl.value);
      this.valueControl.setValue('');
    }
  }

  /** Starts a manual add in `column`. */
  addTo(column: string): void {
    this.columnControl.setValue(column);
    this.valueInput?.nativeElement.focus();
  }
}
